import React, { createContext, useContext, useEffect, useState } from "react";
import { useLiveSessionsStream } from "./useLiveSessionsStream";

// One stream per educator tab. The side nav's Live Sessions item, the Live
// Sessions page and Session History (End Session, status pushes) read it, so
// the SSE connection is opened once in the educator chrome instead of once per
// reader; the server has no snapshot endpoint to poll instead.
const LiveSessionsContext = createContext(null);

// A dropped stream reconnects within a second or two (deploys, proxies), and
// the last snapshot still stands meanwhile. Only a feed down for longer than
// this is an outage readers should own up to.
export const OUTAGE_GRACE_MS = 10000;

const DISABLED = Object.freeze({
  status: "disabled",
  outage: false,
  sessions: {},
  alertProgress: {},
  statusEvents: {},
  tombstones: {},
  snapshotCount: 0,
  transcripts: {},
  endSession: () => {},
});

export const LiveSessionsProvider = ({ enabled = true, children }) => {
  const stream = useLiveSessionsStream({ enabled });
  const up = stream.status === "live" || stream.status === "disabled";
  const [outage, setOutage] = useState(false);
  // Keyed on up/down only, so flapping between error and connecting while
  // reconnecting doesn't restart the grace period.
  useEffect(() => {
    if (up) {
      setOutage(false);
      return undefined;
    }
    const timer = setTimeout(() => setOutage(true), OUTAGE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [up]);

  return (
    <LiveSessionsContext.Provider value={{ ...stream, outage }}>{children}</LiveSessionsContext.Provider>
  );
};

// Outside the educator chrome (the admin tool's impersonated Session
// Reports) there is no provider and the stream reads as disabled, which is
// what the page did for admin views before.
export const useLiveSessions = () => useContext(LiveSessionsContext) || DISABLED;
